import { call } from '@advanza/api'
import clsx from 'clsx'
import {
    INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS,
    INVALIDATE_KEYWORD_MAPPINGS,
} from 'components/keywordMappings/keywordMappingModel'
import FormattedTime from 'components/ui/FormattedTime'
import { useAppDispatch } from 'hooks/hooks'
import { useInterval } from 'hooks/miscHooks'
import { KeywordMappingImport, readKeywordMappingImports } from 'interfaces/KeywordMappingImport'
import { useCallback, useEffect, useRef, useState } from 'react'

interface KeywordMappingImportsProps {
    refreshKey: number
    watchImportId: number | null
}

const POLL_INTERVAL_MS = 2000
const EARLIER_IMPORTS_ID = 'keyword-mapping-earlier-imports'

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readImportListResponse(value: unknown): KeywordMappingImport[] {
    if (!isRecord(value)) return []
    return readKeywordMappingImports(value.items)
}

function formatCount(value: number): string {
    return value.toLocaleString('nl-NL')
}

function isActivelyImporting(item: KeywordMappingImport): boolean {
    if (item.stalled === true) return false
    if (item.status === 'uploading') return true
    if (item.status === 'queued') return true
    if (item.status === 'running') return true
    return false
}

function releaseEndedImports(watched: Set<number>, items: KeywordMappingImport[]): boolean {
    let ended = false
    for (const item of items) {
        if (isActivelyImporting(item)) {
            watched.add(item.keyword_mapping_import_id)
            continue
        }
        if (watched.delete(item.keyword_mapping_import_id)) {
            ended = true
        }
    }
    return ended
}

// Outlives route changes with the list cache, and a full reload clears it with the store.
let endedImportWatermark: number | null = null

interface EndedImportWatermarkDecision {
    nextWatermark: number | null
    shouldInvalidate: boolean
}

function highestEndedImportId(items: KeywordMappingImport[]): number | null {
    let highestId: number | null = null
    for (const item of items) {
        if (isActivelyImporting(item)) continue
        if (highestId === null || item.keyword_mapping_import_id > highestId) {
            highestId = item.keyword_mapping_import_id
        }
    }
    return highestId
}

function decideEndedImportWatermark(
    watermark: number | null,
    items: KeywordMappingImport[],
): EndedImportWatermarkDecision {
    const highestEndedId = highestEndedImportId(items)
    if (highestEndedId === null) {
        // With nothing ended yet, 0 makes the first import to end count as new.
        return { nextWatermark: watermark ?? 0, shouldInvalidate: false }
    }
    if (watermark === null) {
        return { nextWatermark: highestEndedId, shouldInvalidate: false }
    }
    if (highestEndedId > watermark) {
        return { nextWatermark: highestEndedId, shouldInvalidate: true }
    }
    return { nextWatermark: watermark, shouldInvalidate: false }
}

function failedStatus(errorMessage: string | null): string {
    if (errorMessage === null || errorMessage === '') return 'Failed'
    return `Failed: ${errorMessage}`
}

function importStatusText(item: KeywordMappingImport): string {
    if (item.stalled === true) return 'Stopped making progress'
    if (item.status === 'uploading') return 'Uploading'
    if (item.status === 'queued') return 'Waiting to start'
    if (item.status === 'running' && item.total_rows === 0) return 'Reading the file'
    if (item.status === 'running') {
        return `Importing ${formatCount(item.processed_rows)} of ${formatCount(item.total_rows)} rows`
    }
    if (item.status === 'done') return 'Done'
    return failedStatus(item.error_message)
}

function importByline(name: string | null): string {
    if (name === null || name === '') return ''
    return `by ${name} · `
}

function showsRunningProgress(item: KeywordMappingImport): boolean {
    if (item.stalled === true) return false
    if (item.status !== 'running') return false
    return item.total_rows > 0
}

function percentComplete(completed: number, total: number): number {
    if (total < 1) return 0
    const ratio = completed / total
    if (ratio < 0) return 0
    if (ratio > 1) return 100
    return Math.round(ratio * 100)
}

function reportHref(importId: number): string {
    return `${window.location.origin}/api/office/keyword-mappings/import-report?importId=${importId}`
}

interface ImportProgressBarProps {
    processedRows: number
    totalRows: number
}

const ImportProgressBar = ({ processedRows, totalRows }: ImportProgressBarProps) => {
    const valueNow = processedRows > totalRows ? totalRows : processedRows
    const percent = percentComplete(valueNow, totalRows)

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalRows}
            aria-valuenow={valueNow}
            aria-label="Import progress"
            className="h-2 w-full overflow-hidden rounded bg-[#eaeaea]">
            <div className="h-full bg-[#5a8bfc]" style={{ width: `${percent}%` }} />
        </div>
    )
}

interface ImportListItemProps {
    item: KeywordMappingImport
}

const ImportListItem = ({ item }: ImportListItemProps) => {
    const showCounts = item.total_rows > 0
    const showReport = item.reportAvailable === true

    return (
        <li className="flex flex-col gap-1">
            <div>
                <span className="font-medium">{item.file_name}</span>
                {' · '}
                <span aria-live="polite">{importStatusText(item)}</span>
            </div>
            <p className="m-0 text-sm text-[#666666]">
                {importByline(item.createdByName)}
                <FormattedTime date={item.created} format="d MMM yyyy, HH:mm" />
            </p>
            {showsRunningProgress(item) && (
                <ImportProgressBar
                    processedRows={item.processed_rows}
                    totalRows={item.total_rows}
                />
            )}
            {(showCounts || showReport) && (
                <p className="m-0 text-sm text-[#666666]">
                    {showCounts && (
                        <>
                            Added {formatCount(item.added_rows)} · Updated{' '}
                            {formatCount(item.updated_rows)} · Unchanged{' '}
                            {formatCount(item.unchanged_rows)} · Failed{' '}
                            {formatCount(item.failed_rows)} · Duplicates{' '}
                            {formatCount(item.duplicate_rows)}
                        </>
                    )}
                    {showCounts && showReport && ' · '}
                    {showReport && (
                        <a
                            href={reportHref(item.keyword_mapping_import_id)}
                            className="text-sm font-medium text-[#5a8bfc] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a8bfc]"
                            aria-label={`Download report for ${item.file_name}`}>
                            Download report
                        </a>
                    )}
                </p>
            )}
        </li>
    )
}

const KeywordMappingImports = ({ refreshKey, watchImportId }: KeywordMappingImportsProps) => {
    const dispatch = useAppDispatch()
    const [items, setItems] = useState<KeywordMappingImport[]>([])
    const [earlierOpen, setEarlierOpen] = useState(false)
    const activeRef = useRef(true)
    const requestSeqRef = useRef(0)
    const watchedIdsRef = useRef(new Set<number>())

    useEffect(() => {
        activeRef.current = true
        return () => {
            activeRef.current = false
        }
    }, [])

    const loadImports = useCallback(() => {
        const seq = requestSeqRef.current + 1
        requestSeqRef.current = seq

        void call('office/keyword-mappings/import-list')
            .then((response: unknown) => {
                const next = readImportListResponse(response)
                const decision = decideEndedImportWatermark(endedImportWatermark, next)
                endedImportWatermark = decision.nextWatermark

                const isCurrent = activeRef.current && seq === requestSeqRef.current
                const ended = isCurrent && releaseEndedImports(watchedIdsRef.current, next)
                if (isCurrent) setItems(next)
                if (ended || decision.shouldInvalidate) {
                    dispatch({ type: INVALIDATE_KEYWORD_MAPPINGS })
                    dispatch({ type: INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS })
                }
            })
            .catch(() => undefined)
    }, [dispatch])

    useEffect(() => {
        if (watchImportId !== null) {
            watchedIdsRef.current.add(watchImportId)
        }
        loadImports()
    }, [refreshKey, watchImportId, loadImports])

    const shouldPoll = items.some(isActivelyImporting)
    useInterval(loadImports, shouldPoll ? POLL_INTERVAL_MS : null)

    if (items.length === 0) return null

    const visibleItems = items.filter((item, index) => index === 0 || isActivelyImporting(item))
    const earlierItems = items.filter(
        (item, index) => index > 0 && isActivelyImporting(item) === false,
    )

    return (
        <section
            className="mb-4 rounded-lg border border-[#e0e0e0] bg-base-white p-4"
            aria-labelledby="keyword-mapping-imports-heading">
            <h2 id="keyword-mapping-imports-heading" className="m-0 mb-3 text-base font-semibold">
                Recent imports
            </h2>
            <div className="flex flex-col gap-3">
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                    {visibleItems.map((item) => (
                        <ImportListItem key={item.keyword_mapping_import_id} item={item} />
                    ))}
                </ul>
                {earlierItems.length > 0 && (
                    <>
                        <ul
                            id={EARLIER_IMPORTS_ID}
                            className={clsx(
                                'm-0 list-none flex-col gap-3 p-0',
                                earlierOpen ? 'flex' : 'hidden',
                            )}>
                            {earlierItems.map((item) => (
                                <ImportListItem key={item.keyword_mapping_import_id} item={item} />
                            ))}
                        </ul>
                        <button
                            type="button"
                            className="cursor-pointer self-start border-0 bg-base-transparent p-0 text-sm font-medium text-[#5a8bfc] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a8bfc]"
                            aria-expanded={earlierOpen}
                            aria-controls={EARLIER_IMPORTS_ID}
                            onClick={() => setEarlierOpen((open) => !open)}>
                            {earlierOpen
                                ? 'Hide earlier imports'
                                : `Show earlier imports (${earlierItems.length})`}
                        </button>
                    </>
                )}
            </div>
        </section>
    )
}

export default KeywordMappingImports
